import { useRef, useState } from 'react'
import { Report } from 'notiflix/build/notiflix-report-aio'
import { FaBan, FaFileExcel, FaTrashAlt, FaUpload } from 'react-icons/fa'
import api from '../../../helpers/apiRequester'
import { useIsSentinelUser } from '../../../authUserProvider'
import styles from './styles.module.scss'

interface Props {
    // introducerSelector key the backend scopes the run to, e.g. 'PCP Sentinel', 'pcpReferral'.
    introducer: string
    // Human-readable name for copy, e.g. 'PCP Referral'.
    introducerLabel: string
}

type ActionMode = 'cancel' | 'delete'

interface PreviewClient {
    id: number
    email: string
    clientRef: string | null
    name: string | null
    cases: number
    alreadyCancelled?: number
    toCancel?: number
}

interface PreviewResponse {
    success: boolean
    preview: boolean
    introducerLabel: string
    requestedEmails: number
    invalidCount: number
    matchedClients: number
    matchedCases: number
    unmatchedEmails: string[]
    unmatchedCount: number
    clients: PreviewClient[]
    // cancel only
    casesToCancel?: number
    casesAlreadyCancelled?: number
    casesWithoutState?: number
    reason?: string
    // delete only
    tableCounts?: Record<string, number>
    s3PrefixCount?: number
    s3KeyCount?: number
    linkedFundingEmails?: string[]
    linkedFundingCount?: number
}

interface ResultResponse extends PreviewResponse {
    message: string
    updated?: number
    logged?: number
    deleted?: Record<string, number>
    s3?: { deleted: number; failed: { key: string; error: string; bucket?: string }[] }
}

const ACTION_CONFIG: Record<
    ActionMode,
    {
        title: string
        icon: JSX.Element
        description: string
        endpoint: string
        confirmWord: string
        confirmBody: string
        previewButton: string
        runButton: string
        runningButton: string
        successTitle: string
    }
> = {
    cancel: {
        title: 'Cancel clients by spreadsheet',
        icon: <FaBan aria-hidden="true" />,
        description:
            'Upload a CSV or Excel file with an email column. Every case belonging to those clients in this ' +
            'introducer is marked cancelled (lead_states) and logged. No cancellation letters are sent — use the ' +
            'per-case Cancel action when a letter is needed.',
        endpoint: '/introducer-bulk-cancel-by-email-csv',
        confirmWord: 'cancel',
        confirmBody: 'CANCEL',
        previewButton: 'Preview cancellation',
        runButton: 'Cancel matched cases',
        runningButton: 'Cancelling…',
        successTitle: 'Cases cancelled',
    },
    delete: {
        title: 'Delete client data by spreadsheet',
        icon: <FaTrashAlt aria-hidden="true" />,
        description:
            'For clients who have asked us to remove their data. Upload a CSV or Excel file with an email column: ' +
            'every record linked to those clients in this introducer (client, leads, states, signatures, uploads…) ' +
            'and all of their S3 documents are permanently deleted. Only a hashed audit entry is kept. ' +
            'Funding-monitor records are not touched.',
        endpoint: '/introducer-client-data-delete-by-email-csv',
        confirmWord: 'delete',
        confirmBody: 'DELETE',
        previewButton: 'Preview deletion',
        runButton: 'Delete clients and documents',
        runningButton: 'Deleting…',
        successTitle: 'Client data deleted',
    },
}

const MAX_LISTED_EMAILS = 20

const fileToBase64 = async (file: File) => {
    const buffer = await file.arrayBuffer()
    let binary = ''
    const bytes = new Uint8Array(buffer)
    const chunkSize = 0x8000
    for (let i = 0; i < bytes.length; i += chunkSize) {
        binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunkSize)))
    }
    return btoa(binary)
}

const errorMessage = (error: any, fallback: string) => error?.response?.data?.message || error?.message || fallback

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`

const SpreadsheetActionCard = ({ mode, introducer, introducerLabel }: Props & { mode: ActionMode }) => {
    const config = ACTION_CONFIG[mode]
    const inputRef = useRef<HTMLInputElement>(null)
    const [file, setFile] = useState<File | null>(null)
    const [reason, setReason] = useState('')
    const [preview, setPreview] = useState<PreviewResponse | null>(null)
    const [result, setResult] = useState<ResultResponse | null>(null)
    const [confirmText, setConfirmText] = useState('')
    const [previewing, setPreviewing] = useState(false)
    const [running, setRunning] = useState(false)

    const reset = () => {
        setPreview(null)
        setResult(null)
        setConfirmText('')
    }

    const chooseFile = (next: File | null) => {
        setFile(next)
        reset()
    }

    const clearFile = () => {
        chooseFile(null)
        if (inputRef.current) inputRef.current.value = ''
    }

    const requestBody = async (extra: Record<string, unknown>) => ({
        introducer,
        fileData: await fileToBase64(file as File),
        filename: file?.name,
        ...(mode === 'cancel' && reason.trim() ? { reason: reason.trim() } : {}),
        ...extra,
    })

    const runPreview = async () => {
        if (!file) return
        setPreviewing(true)
        setResult(null)
        try {
            const response = await api.post(config.endpoint, await requestBody({ preview: true }))
            if (!response.data?.success) throw new Error(response.data?.message || 'Preview failed')
            setPreview(response.data)
            setConfirmText('')
        } catch (error: any) {
            setPreview(null)
            Report.failure('Preview failed', errorMessage(error, 'Could not read the spreadsheet.'), 'Okay')
        } finally {
            setPreviewing(false)
        }
    }

    const runAction = async () => {
        if (!file || !preview || confirmText.trim().toLowerCase() !== config.confirmWord) return
        setRunning(true)
        try {
            const response = await api.post(config.endpoint, await requestBody({ confirm: config.confirmBody }))
            if (!response.data?.success) throw new Error(response.data?.message || 'Request failed')
            setResult(response.data)
            setPreview(null)
            setConfirmText('')
            Report.success(config.successTitle, response.data.message || 'Done.', 'Okay')
        } catch (error: any) {
            Report.failure('Action failed', errorMessage(error, 'The request did not complete.'), 'Okay')
        } finally {
            setRunning(false)
        }
    }

    const actionable = mode === 'cancel' ? (preview?.casesToCancel ?? 0) > 0 : (preview?.matchedClients ?? 0) > 0
    const confirmed = confirmText.trim().toLowerCase() === config.confirmWord
    const busy = previewing || running

    return (
        <div className={`${styles.card} ${mode === 'delete' ? styles.cardDanger : ''}`}>
            <h3 className={styles.cardTitle}>
                {config.icon} {config.title}
            </h3>
            <p className={styles.helpText}>{config.description}</p>

            <div className={styles.controls}>
                <label className={styles.fileLabel}>
                    <FaUpload aria-hidden="true" />
                    <span>{file ? file.name : 'Choose CSV / XLSX'}</span>
                    <input
                        ref={inputRef}
                        type="file"
                        accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                        disabled={busy}
                        onChange={(e) => chooseFile(e.target.files?.[0] || null)}
                    />
                </label>
                {mode === 'cancel' && (
                    <input
                        className={styles.reasonInput}
                        type="text"
                        maxLength={45}
                        placeholder="Cancellation reason (default: Client request)"
                        value={reason}
                        disabled={busy}
                        onChange={(e) => {
                            setReason(e.target.value)
                            if (preview) setPreview(null)
                        }}
                    />
                )}
                <button
                    type="button"
                    className={mode === 'delete' ? styles.dangerButton : styles.primaryButton}
                    disabled={!file || busy}
                    onClick={runPreview}
                >
                    {previewing ? 'Previewing…' : config.previewButton}
                </button>
                {file && (
                    <button type="button" className={styles.linkButton} disabled={busy} onClick={clearFile}>
                        Clear
                    </button>
                )}
            </div>

            {preview && (
                <div className={styles.preview}>
                    <p className={styles.previewHeadline}>
                        <FaFileExcel aria-hidden="true" /> {plural(preview.requestedEmails, 'email')} read
                        {preview.invalidCount > 0 ? ` (${plural(preview.invalidCount, 'invalid value')} skipped)` : ''}.
                        Matched <strong>{plural(preview.matchedClients, 'client')}</strong> and{' '}
                        <strong>{plural(preview.matchedCases, 'case')}</strong> in{' '}
                        {preview.introducerLabel || introducerLabel}.
                    </p>

                    {mode === 'cancel' && (
                        <ul className={styles.statList}>
                            <li>
                                <strong>{preview.casesToCancel ?? 0}</strong> case(s) will be cancelled with reason{' '}
                                <em>{preview.reason}</em>
                            </li>
                            <li>
                                <strong>{preview.casesAlreadyCancelled ?? 0}</strong> case(s) are already cancelled and
                                will be left as they are
                            </li>
                            {(preview.casesWithoutState ?? 0) > 0 && (
                                <li className={styles.warning}>
                                    {preview.casesWithoutState} case(s) have no lead_states row and cannot be cancelled
                                    here
                                </li>
                            )}
                        </ul>
                    )}

                    {mode === 'delete' && preview.tableCounts && (
                        <>
                            <p className={styles.previewSub}>Rows that will be permanently deleted:</p>
                            <ul className={styles.tableCounts}>
                                {Object.entries(preview.tableCounts)
                                    .filter(([, n]) => n > 0)
                                    .map(([table, n]) => (
                                        <li key={table}>
                                            <code>{table}</code> <strong>{n}</strong>
                                        </li>
                                    ))}
                            </ul>
                            <p className={styles.previewSub}>
                                S3: {plural(preview.s3PrefixCount ?? 0, 'document folder')} and{' '}
                                {plural(preview.s3KeyCount ?? 0, 'named file')} will be emptied, plus any uploads
                                referenced from the deleted rows.
                            </p>
                            {(preview.linkedFundingCount ?? 0) > 0 && (
                                <p className={styles.warning}>
                                    {plural(preview.linkedFundingCount ?? 0, 'email')} also exist in funding-monitor.
                                    Those records are shared with funders and are NOT deleted by this tool.
                                </p>
                            )}
                        </>
                    )}

                    {preview.unmatchedCount > 0 && (
                        <p className={styles.previewSub}>
                            {plural(preview.unmatchedCount, 'email')} not found in {introducerLabel}:{' '}
                            {preview.unmatchedEmails.slice(0, MAX_LISTED_EMAILS).join(', ')}
                            {preview.unmatchedEmails.length > MAX_LISTED_EMAILS ? ', …' : ''}
                        </p>
                    )}

                    {preview.clients.length > 0 && (
                        <div className={styles.tableWrap}>
                            <table className={styles.clientTable}>
                                <thead>
                                    <tr>
                                        <th>Email</th>
                                        <th>Name</th>
                                        <th>Client ref</th>
                                        <th>Cases</th>
                                        {mode === 'cancel' && <th>To cancel</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {preview.clients.slice(0, 200).map((client) => (
                                        <tr key={client.id}>
                                            <td>{client.email}</td>
                                            <td>{client.name || '—'}</td>
                                            <td>{client.clientRef || client.id}</td>
                                            <td>{client.cases}</td>
                                            {mode === 'cancel' && <td>{client.toCancel ?? 0}</td>}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {preview.clients.length > 200 && (
                                <p className={styles.previewSub}>
                                    Showing the first 200 of {preview.clients.length} clients.
                                </p>
                            )}
                        </div>
                    )}

                    {actionable ? (
                        <div className={styles.confirmRow}>
                            <label>
                                Type <strong>{config.confirmWord}</strong> to confirm
                                <input
                                    type="text"
                                    value={confirmText}
                                    autoComplete="off"
                                    disabled={running}
                                    onChange={(e) => setConfirmText(e.target.value)}
                                />
                            </label>
                            <button
                                type="button"
                                className={styles.dangerButton}
                                disabled={!confirmed || running}
                                onClick={runAction}
                            >
                                {running ? config.runningButton : config.runButton}
                            </button>
                        </div>
                    ) : (
                        <p className={styles.previewSub}>Nothing to do for this spreadsheet.</p>
                    )}
                </div>
            )}

            {result && (
                <div className={styles.result}>
                    <p>{result.message}</p>
                    {mode === 'delete' && result.s3?.failed?.length ? (
                        <p className={styles.warning}>
                            {plural(result.s3.failed.length, 'S3 object')} could not be deleted — check the server log.
                        </p>
                    ) : null}
                </div>
            )}
        </div>
    )
}

// Sentinel-only "Client Actions by Spreadsheet" section shown on every PCP
// introducer overview page. Both tools are scoped to the page's introducer
// database and go preview → typed confirmation → run.
export const IntroducerClientActions = ({ introducer, introducerLabel }: Props) => {
    const isSentinelUser = useIsSentinelUser()
    if (!isSentinelUser) return null

    return (
        <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Client Actions by Spreadsheet</h2>
            <p className={styles.helpText}>
                Bulk tools for {introducerLabel} clients. Upload a list of email addresses, review exactly what will
                change, then confirm.
            </p>
            <div className={styles.cards}>
                <SpreadsheetActionCard mode="cancel" introducer={introducer} introducerLabel={introducerLabel} />
                <SpreadsheetActionCard mode="delete" introducer={introducer} introducerLabel={introducerLabel} />
            </div>
        </section>
    )
}

export default IntroducerClientActions
